import { Type, Index } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

const MapValueError = (error) => ({
    message: error.message,
    path: error.path,
    errors: error.errors.map(iterator => mapValueErrorIterator(iterator))
});
const mapValueErrorIterator = (iterator) => [...iterator].map(error => MapValueError(error));

class DataLayerError extends Error {
    constructor({ message, errors = [] }) {
        super(message);
        this.message = message;
        this.validationErrors = mapValueErrorIterator(errors);
    }
}

/**
 * ATTENTION. This code was AUTO GENERATED by schema2typebox.
 * While I don't know your use case, there is a high chance that direct changes
 * to this file get lost. Consider making changes to the underlying JSON schema
 * you use to generate this file instead. The default file is called
 * "schema.json", perhaps have a look there! :]
 */
const types = Type.Object({
    pageData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "pageData", readOnly: true })),
        href: Type.String({
            description: "The page's href from 'window.location.href'.",
        }),
        pagePath: Type.String({
            description: "The page's pathname from 'window.location.pathname'.",
        }),
        pageTitle: Type.String({
            description: "The title of the page from 'window.document.title'.",
        }),
        pageName: Type.Optional(Type.String({
            description: "The name identifier for the current page, used for analytics and tracking purposes. It should be from 'window.location.pathname'.",
        })),
        pageType: Type.Optional(Type.String({
            description: "The type or category of the current page",
        })),
        siteContentHierarchy: Type.Optional(Type.String({
            description: "The hierarchical path representing the page's position within the site structure for content categorization.",
        })),
        viewportDimensions: Type.Optional(Type.Union([
            Type.String({
                pattern: "^\\d*\\:\\d*$",
                description: "Auto-filled by middleware using window.innerWidth and innerHeight if not provided. May be null if unavailable.",
                examples: ["1920:1080"],
            }),
            Type.Null({
                pattern: "^\\d*\\:\\d*$",
                description: "Auto-filled by middleware using window.innerWidth and innerHeight if not provided. May be null if unavailable.",
                examples: ["1920:1080"],
            }),
        ])),
        pageLoadSpeedFirstByte: Type.Optional(Type.Number({
            description: "Time to first byte (TTFB) in milliseconds (ms) measuring server response time.",
        })),
        pageLoadSpeedDomReady: Type.Optional(Type.Number({
            description: "Time in milliseconds from navigation start until the DOM is ready.",
        })),
        localTimestamp: Type.String({
            pattern: "^([\\+-]?\\d{4}(?!\\d{2}\\b))((-?)((0[1-9]|1[0-2])(\\3([12]\\d|0[1-9]|3[01]))?|W([0-4]\\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\\d|[12]\\d{2}|3([0-5]\\d|6[1-6])))([T\\s]((([01]\\d|2[0-3])((:?)[0-5]\\d)?|24\\:?00)([\\.,]\\d+(?!:))?)?(\\17[0-5]\\d([\\.,]\\d+)?)?([zZ]|([\\+-])([01]\\d|2[0-3]):?([0-5]\\d)?)?)?)?$",
            description: "Local timestamp in ISO 8601 format when the event occurred.",
            examples: ["2000-01-00T00:00:00.839Z"],
        }),
        hostname: Type.String({
            description: "The hostname of the page from 'window.location.hostname'.",
        }),
        queryString: Type.String({
            description: "The page's query string from.",
        }),
        browseCurrency: Type.String({
            description: "Current browsing currency as an ISO 4217 alpha-3 currency code.",
        }),
        browseCountry: Type.String({
            description: "Current browsing country as an ISO 3166 alpha-2 country code.",
        }),
        language: Type.Optional(Type.String({ description: "Current browsing language." })),
        channel: Type.Union([
            Type.Literal("women"),
            Type.Literal("men"),
            Type.Literal("default"),
            Type.Literal("checkout"),
        ], {
            $schema: "http://json-schema.org/draft-07/schema#",
            description: "One of women, men, checkout or default.",
        }),
        deviceType: Type.Union([
            Type.Literal("desktop"),
            Type.Literal("mobile"),
            Type.Literal("tablet"),
        ], { description: "One of desktop, mobile, or tablet." }),
        env: Type.Union([Type.Literal("test"), Type.Literal("prod")], {
            description: "The environment in which the code is running.'test' or 'prod'.",
        }),
        referringUrl: Type.String({
            description: "The referrer of the page.",
        }),
        marketingData: Type.Optional(Type.Object({
            affid: Type.Optional(Type.Union([
                Type.String({
                    description: "Referring affiliate ID obtained from the affid query string parameter or query fragment.",
                }),
                Type.Null({
                    description: "Referring affiliate ID obtained from the affid query string parameter or query fragment.",
                }),
            ])),
            nlid: Type.Optional(Type.Union([
                Type.String({
                    description: "Navigation link identifier (nlid) for tracking navigation source.",
                }),
                Type.Null({
                    description: "Navigation link identifier (nlid) for tracking navigation source.",
                }),
            ])),
            googleCid: Type.Optional(Type.Union([
                Type.String({
                    description: "Google Client ID for tracking Google Analytics sessions.",
                }),
                Type.Null({
                    description: "Google Client ID for tracking Google Analytics sessions.",
                }),
            ])),
            gclid: Type.Optional(Type.Union([
                Type.String({
                    description: "Google click ID for tracking Google Ads campaigns.",
                }),
                Type.Null({
                    description: "Google click ID for tracking Google Ads campaigns.",
                }),
            ])),
            ranSiteId: Type.Optional(Type.Union([
                Type.String({
                    description: "An identifier of the publisher/site from Rakuten's affiliate network.",
                }),
                Type.Null({
                    description: "An identifier of the publisher/site from Rakuten's affiliate network.",
                }),
            ])),
            ranLandingDate: Type.Optional(Type.Union([
                Type.String({
                    description: "The date that the customer landed on ASOS via a referral from Rakuten.",
                }),
                Type.Null({
                    description: "The date that the customer landed on ASOS via a referral from Rakuten.",
                }),
            ])),
            ranMid: Type.Optional(Type.Union([
                Type.String({
                    description: "The merchant identifier of the publisher from Rakuten's affiliate network.",
                }),
                Type.Null({
                    description: "The merchant identifier of the publisher from Rakuten's affiliate network.",
                }),
            ])),
            awinId: Type.Optional(Type.Union([
                Type.String({
                    description: "An identifier of the affiliate that is part of the Affiliate Window network that referred the customer.",
                }),
                Type.Null({
                    description: "An identifier of the affiliate that is part of the Affiliate Window network that referred the customer.",
                }),
            ])),
            admitadId: Type.Optional(Type.Union([
                Type.String({
                    description: "An identifier of the affiliate that is part of the Admitad network that referred the customer.",
                }),
                Type.Null({
                    description: "An identifier of the affiliate that is part of the Admitad network that referred the customer.",
                }),
            ])),
            fbAdId: Type.Optional(Type.Union([
                Type.String({
                    description: "The Facebook ad ID for the ad created by Smartly | AI Advertising Platform.",
                }),
                Type.Null({
                    description: "The Facebook ad ID for the ad created by Smartly | AI Advertising Platform.",
                }),
            ])),
            fbLandingDate: Type.Optional(Type.Union([
                Type.String({
                    description: "The time that the customer clicked on the Facebook ad created by Smartly.",
                }),
                Type.Null({
                    description: "The time that the customer clicked on the Facebook ad created by Smartly.",
                }),
            ])),
            impactRadiusClickId: Type.Optional(Type.Union([
                Type.String({
                    description: "The unique click ID associated with a referral from the Impact Radius affiliate network.",
                }),
                Type.Null({
                    description: "The unique click ID associated with a referral from the Impact Radius affiliate network.",
                }),
            ])),
            asosConnectTransactionId: Type.Optional(Type.Union([
                Type.String({
                    description: "A unique identifier for the referral from ASOS Connect.",
                }),
                Type.Null({
                    description: "A unique identifier for the referral from ASOS Connect.",
                }),
            ])),
            buttonId: Type.Optional(Type.Union([
                Type.String({
                    description: "An internal Button (mobile commerce partner) identifier.",
                }),
                Type.Null({
                    description: "An internal Button (mobile commerce partner) identifier.",
                }),
            ])),
            crealyticsId: Type.Optional(Type.Union([
                Type.String({
                    description: "An internal Crealytics identifier.",
                }),
                Type.Null({
                    description: "An internal Crealytics identifier.",
                }),
            ])),
            intelligentReachId: Type.Optional(Type.Union([
                Type.String({
                    description: "An identifier of the publisher specific to the publisher or affiliate using the Intelligent Reach product feed.",
                }),
                Type.Null({
                    description: "An identifier of the publisher specific to the publisher or affiliate using the Intelligent Reach product feed.",
                }),
            ])),
            publisherReference: Type.Optional(Type.Union([
                Type.String({
                    description: "The identifier of the exact publisher that referred the customer.",
                }),
                Type.Null({
                    description: "The identifier of the exact publisher that referred the customer.",
                }),
            ])),
            zanoxPartnerId: Type.Optional(Type.Union([
                Type.String({
                    description: "An identifier of the publisher specific to the Zanox network.",
                }),
                Type.Null({
                    description: "An identifier of the publisher specific to the Zanox network.",
                }),
            ])),
        }, { description: "Object containing marketing data." })),
        customerData: Type.Optional(Type.Object({
            isAuthenticated: Type.Optional(Type.Boolean({
                description: "Whether the customer is authenticated.",
            })),
            customerId: Type.Optional(Type.String({ description: "The customer's ID." })),
            firstTimeBuyer: Type.Optional(Type.Boolean({
                description: "Whether the customer is a first time buyer.",
            })),
            premierStatus: Type.Optional(Type.Boolean({
                description: "The customer's premier status.",
            })),
            studentStatus: Type.Optional(Type.Boolean({
                description: "The customer's student status.",
            })),
            hashedEmail: Type.Optional(Type.Union([
                Type.String({
                    description: "SHA256 hash of user email. See: https://developers.google.com/google-ads/api/docs/conversions/enhance-conversions#normalization_and_hashing.",
                }),
                Type.Null({
                    description: "SHA256 hash of user email. See: https://developers.google.com/google-ads/api/docs/conversions/enhance-conversions#normalization_and_hashing.",
                }),
            ])),
            normalisedHashedEmail: Type.Optional(Type.Union([
                Type.String({
                    description: "Legacy field for checkout, Normalized SHA256 hash of user email. See: https://developers.google.com/google-ads/api/docs/conversions/enhance-conversions#normalization_and_hashing.",
                }),
                Type.Null({
                    description: "Legacy field for checkout, Normalized SHA256 hash of user email. See: https://developers.google.com/google-ads/api/docs/conversions/enhance-conversions#normalization_and_hashing.",
                }),
            ])),
            firstName: Type.Optional(Type.String({
                description: "Legacy field for checkout, un-hashed first name.",
            })),
            hashedFirstName: Type.Optional(Type.String({
                description: "SHA256 hash of user first name.",
            })),
            hashedLastName: Type.Optional(Type.String({ description: "SHA256 hash of user last name." })),
            hashedPhoneNumber: Type.Optional(Type.Union([
                Type.String({
                    description: "SHA256 hash of user phone number (Format according to the E164 standard).",
                }),
                Type.Null({
                    description: "SHA256 hash of user phone number (Format according to the E164 standard).",
                }),
            ])),
            geoIpCountryCode: Type.Optional(Type.String({ description: "User country based on geo ip." })),
            asosWorldTier: Type.Optional(Type.String({
                description: "The customer's ASOS World tier.",
            })),
            customerSegments: Type.Optional(Type.Array(Type.String(), {
                description: "An array of IDs for the segments to which a customer belongs",
            })),
        }, { description: "Object containing customer profile data." })),
        webVersion: Type.Optional(Type.String({
            description: "The app or web version. The format of this variable is up to the engineering teams and can change over time.",
        })),
        ctaRef: Type.Optional(Type.String({
            description: "The call to action reference that customers clicked on to react the current page.",
        })),
        pageComponents: Type.Optional(Type.Array(Type.String({
            description: "The name of a unique page component.",
        }), {
            description: "An array containing a list of unique page component names.",
        })),
        attributionCategory: Type.Optional(Type.String({
            description: "A value identifying a page or component, used to attribute PDP interactions to that page. Such as the category from which the visitor clicked to view a PDP; or the specific component that led to a PDP view.",
        })),
    }, {
        description: "The pageData event is triggered on every page load or navigation change, delivering essential context about the current page. It provides initial data required for rendering, analytics, and other page-specific logic.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/pageData.json",
        additionalProperties: false,
    })),
    customerPremierData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerPremierData", readOnly: true })),
        premierStatus: Type.String({
            description: "Premier status of the customer. Either 'active' or 'inactive'.",
        }),
        freeTrialAvailable: Type.Boolean({
            description: "Indicates if a free trial is available for the customer.",
        }),
    }, {
        description: "The customerPremierData event is triggered when customer Premier data is retrieved by the consuming app. It provides data that pertains to the customers current Premier status and free trial eligibility.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerPremierData.json",
        additionalProperties: false,
    })),
    customerSegmentsData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerSegmentsData", readOnly: true })),
        segments: Type.Array(Type.String({
            description: "An array of segments the user belongs to from the customer audience API",
        }), {
            description: "The segments to which the customer belongs. Returns an empty array if user is in no segments or not logged in. This is powered by the customer audience API.",
        }),
    }, {
        description: "The customerSegmentsData event is triggered when customer segments data is retrieved by the consuming app. It provides the list of customer segments as a comma separated string.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerSegmentsData.json",
        additionalProperties: false,
    })),
    customerLoyaltyData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerLoyaltyData", readOnly: true })),
        consentStatus: Type.Union([
            Type.String({
                description: "The consent status of the customer",
                examples: ["OptedIn", "OptedOut"],
            }),
            Type.Null({
                description: "The consent status of the customer",
                examples: ["OptedIn", "OptedOut"],
            }),
        ]),
        currentTierId: Type.Union([
            Type.String({
                description: "Identifier for which loyalty scheme they’re on",
                examples: ["GB_LOYT_1", "GB_LOYT_2", "US_LOYT_1"],
            }),
            Type.Null({
                description: "Identifier for which loyalty scheme they’re on",
                examples: ["GB_LOYT_1", "GB_LOYT_2", "US_LOYT_1"],
            }),
        ]),
        tierNumber: Type.Union([
            Type.Number({ description: "The tier number the user is on" }),
            Type.Null({ description: "The tier number the user is on" }),
        ]),
        country: Type.Union([
            Type.String({
                description: "Country code for this loyalty scheme",
            }),
            Type.Null({ description: "Country code for this loyalty scheme" }),
        ]),
    }, {
        description: "The customerLoyaltyData event is triggered when customer loyalty data is retrieved by the consuming app. It provides the customer's loyalty status, tier ID, tier number and customer's country.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerLoyaltyData.json",
        additionalProperties: false,
    })),
    customerStudentData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerStudentData", readOnly: true })),
        isStudent: Type.Boolean({
            description: "Indicates if the customer is a student or not",
        }),
    }, {
        description: "The customerStudentData event is triggered when the student status is retrieved by the consuming app. It provides the student status as a boolean value.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerStudentData.json",
        additionalProperties: false,
    })),
    OneTrustGroupsUpdated: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "OneTrustGroupsUpdated", readOnly: true })),
        OnetrustActiveGroups: Type.String({
            description: "Comma delimited string of the current active category ids as selected by the visitor (or the default setup). You can read more details on what these codes mean here: https://github.com/asosteam/asos-web-consent?tab=readme-ov-file#api",
            examples: ["C0001", "C0001,C0002,C0003"],
        }),
    }, {
        description: "The OneTrustGroupsUpdated event is triggered whenever customer consent changes. It provides a list of groups that indicate the ways a customer has permitted their personal data to be used.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/OneTrustGroupsUpdated.json",
        additionalProperties: false,
    })),
    timeToFirstByteData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "timeToFirstByte", readOnly: true })),
        value: Type.Number({
            description: "Time to first byte (TTFB) in milliseconds (ms) measuring server response time.",
        }),
    }, {
        description: "The timeToFirstByte event is triggered when the time to first byte has finished being calculated. It contains vital information to understanding the page performance.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/timeToFirstByteEvent.json",
        additionalProperties: false,
    })),
    firstContentfulPaintData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "firstContentfulPaint", readOnly: true })),
        value: Type.Number({
            description: "Time to first byte (TTFB) in milliseconds (ms) measuring server response time.",
        }),
    }, {
        description: "The firstContentfulPaintData event is triggered when the first contentful paint timing has finished being calculated. It contains vital information to understanding the page performance.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/firstContentfulPaintEvent.json",
        additionalProperties: false,
    })),
    add_shipping_info: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_shipping_info", readOnly: true })),
        ecommerce: Type.Optional(Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            shipping_tier: Type.Optional(Type.String({
                description: "The shipping tier of the selected option (e.g. Standard Shipping, Next-Day, Express)",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({
                    description: "The category of the item.",
                })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        })),
        section: Type.Optional(Type.String({ default: "deliveryOptions", readOnly: true })),
        deliveryOptionId: Type.Optional(Type.Number({ description: "Integer id of selected option" })),
        isSplitFulfilment: Type.Optional(Type.Boolean({
            description: "True if some items will be delivered by a different option to the one selected.",
        })),
    }, {
        description: "The add_shipping_info event is triggered when selection of a delivery option is made through explicitly clicking the associated radio button. Indicates that a delivery option has been explicitly selected by the customer and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_shipping_info.json",
    })),
    begin_checkout: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "begin_checkout", readOnly: true })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The begin_checkout event is triggered entry / re-entry to Checkout / refresh of checkout page. It provides information about the items in the basket and their total value, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/begin_checkout.json",
        additionalProperties: false,
    })),
    add_to_cart: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_to_cart", readOnly: true })),
        ecommerce: Type.Optional(Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({
                    description: "The category of the item.",
                })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        })),
    }, {
        description: "The add_to_cart event is triggered when a customer clicks on add to bag. It provides information about the product(s) added to the bag and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_to_cart.json",
    })),
    view_item: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "view_item", readOnly: true })),
        productId: Type.Number({ description: "The product ID." }),
        productCode: Type.String({ description: "The product code." }),
        availableSku: Type.Optional(Type.Number({ description: "The number of available SKU." })),
        availableSizes: Type.Optional(Type.Number({ description: "The number of available sizes." })),
        productUrl: Type.String({ description: "The URL of the product." }),
        cid: Type.Union([
            Type.Number({
                description: "The category ID (CID) of the product.",
            }),
            Type.Null({ description: "The category ID (CID) of the product." }),
        ]),
        category: Type.Optional(Type.String({
            description: "RMS Product Type Clean breakdown of all products onsite / category for filtering on site. This is optional only in the case of a M&M product.",
        })),
        variantStockStatus: Type.Optional(Type.Array(Type.Object({
            variantId: Type.Number({
                description: "The product variant id",
            }),
            variantSku: Type.String({
                description: "The product variant SKU",
            }),
            stockStatus: Type.String({
                description: "The stock status of the product variant",
            }),
        }, {
            description: "The stock status of each variant of the product",
            additionalProperties: false,
        }), {
            description: "An array containing the stock status of each variant of the product",
        })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The view_item is triggered on entry / re-entry / refresh of single PDP or Mix and Match PDP Browse application.  It provides information about the product and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/ViewItem.json",
        additionalProperties: false,
    })),
    view_item_list: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "view_item_list", readOnly: true })),
        item_list_name: Type.Union([
            Type.Literal("search"),
            Type.Literal("brand"),
            Type.Literal("category"),
            Type.Literal("wishlist"),
            Type.Literal("plp_carousel"),
            Type.Literal("pdp_ymal"),
            Type.Literal("pdp_pab"),
            Type.Literal("bag_pab"),
            Type.Literal("pdp_btl"),
            Type.Literal("brand_directory"),
        ], {
            description: "Type of listing page. For search pages this is always 'search'. For brand pages this is always 'brand'. For brand directory pages this is always 'brand_directory'. For category pages this is always 'category'. For saved list items this is always 'wishlist'. For any component level lists, it should be one of the following values: 'plp_carousel', 'pdp_ymal', 'pdp_pab', 'bag_pab', 'pdp_btl'",
        }),
        item_list_state: Type.Optional(Type.Union([
            Type.Literal("search"),
            Type.Literal("failed_search"),
            Type.Literal("category"),
            Type.Literal("generic_search"),
            Type.Literal("pagination"),
            Type.Literal("refinement"),
            Type.Literal("sort"),
            Type.Literal("sort_and_refinement"),
        ], {
            description: "The state that the list is in, essential for analytics tracking on category & search pdps\nsearch - Indicates the list has loaded immedietly after the user has done a search\nfailed_search - Indicates the list is a search page with no results\ncategory - Indicates the list is a normal category page\ngeneric_search - Indicates the list is a search page, but not invoked by the searchTerm\npagination - Indicates the list has been paginated\nrefinement - Indicates the list has been refined\nsort - Indicates the list has been sorted\nsort_and_refinement - Indicates the list has been sorted and refined",
        })),
        search_term: Type.Optional(Type.String({ description: "The customer’s search term." })),
        searchType: Type.Optional(Type.String({
            description: "How the customer performed the search",
        })),
        categoryID: Type.Optional(Type.Number({ description: "The category ID" })),
        pageGridSize: Type.Optional(Type.Number({
            description: "The number of product displayed in each column",
        })),
        pageNumber: Type.Optional(Type.Number({ description: "The page number where applicable" })),
        pageSize: Type.Optional(Type.Number({
            description: "The number of products displayed on each page",
        })),
        totalStyles: Type.Optional(Type.Number({
            description: "The total number of products, not just the ones displayed",
        })),
        floor: Type.Optional(Type.Union([
            Type.Literal("men"),
            Type.Literal("women"),
            Type.Literal("search"),
            Type.Literal("default"),
        ], { description: "The customer’s floor." })),
        breadCrumb: Type.Optional(Type.String({
            pattern: ".*[A-Za-zÀ-ÖØ-öø-ÿ0-9 &']+%\\$[A-Za-zÀ-ÖØ-öø-ÿ0-9 &']+(%\\$[A-Za-zÀ-ÖØ-öø-ÿ0-9 &']+)*.*",
            description: "String containing at least one '%$'-separated segment, e.g., 'women%$new' or 'women%$a to z of brands%$asos design'.",
        })),
        refinements: Type.Optional(Type.Object({
            context: Type.Optional(Type.String({
                description: "The refinements the customer used, where applicable",
            })),
            listAppliedRefinements: Type.Optional(Type.Array(Type.Object({
                refinementId: Type.Optional(Type.String({ description: "Facet ID" })),
                refinementName: Type.Optional(Type.String({ description: "Facet name" })),
                selections: Type.Optional(Type.Array(Type.Object({
                    selectionId: Type.Optional(Type.String({ description: "Selection ID" })),
                    selectionName: Type.Optional(Type.String({ description: "Selection name" })),
                }), {
                    description: "The list of selections made within the refinement",
                })),
            }), { description: "The list of applied refinements" })),
            sort: Type.Optional(Type.Object({
                sortName: Type.Optional(Type.String({ description: "Sort name" })),
                sortType: Type.Optional(Type.String({ description: "Sort option" })),
            }, { description: "The sorting applied." })),
        }, { description: "The refinements applied by the customer" })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The view_item_list event is triggered when a list of items is viewed by the customer. It provides the list of items for different lists (search, brand pages, category pages, wishlists) and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/ViewItemListData.json",
        additionalProperties: false,
    })),
    view_cart: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "view_cart", readOnly: true })),
        totalItemCount: Type.Number({
            description: "The total number of items in the basket.",
        }),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The view_cart event is triggered on entry / re-entry / refresh of Bag application. It provides the total number of items in the cart, item values and the cart value, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/view_cart.json",
        additionalProperties: false,
    })),
    add_payment_info: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_payment_info", readOnly: true })),
        ecommerce: Type.Optional(Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            payment_type: Type.Optional(Type.String({
                description: "Payment method currently selected for use with the order.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({
                    description: "The category of the item.",
                })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        })),
        section: Type.Optional(Type.String({ default: "paymentOptions", readOnly: true })),
        isSavedPayment: Type.Optional(Type.Boolean({
            description: "Indicates that the user has previously provided the payment method.",
        })),
    }, {
        description: "The add_payment_info event is triggered when a payment method is selected from the list of payments. It indicates successful entry of a payment method either through a selection of a saved payment method or entry of a new payment method, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_payment_info.json",
    })),
    customerIdentityData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerIdentityData", readOnly: true })),
        customerId: Type.Optional(Type.String({
            description: "Unique identifier for the customer provided by identity token claims.",
        })),
        lastAction: Type.Optional(Type.Union([
            Type.Literal("none"),
            Type.Literal("signUpSocial"),
            Type.Literal("signUpEmail"),
            Type.Literal("linkAccounts"),
            Type.Literal("savedBillingAddress"),
            Type.Literal("savedBillingAndDeliveryAddress"),
            Type.Literal("signInSocial"),
            Type.Literal("signInEmail"),
        ], {
            description: "Represents the lastAction provided by identity token claims.",
        })),
        firstTimeBuyer: Type.Optional(Type.Boolean({
            description: "Indicates if the customer is a first-time buyer provided by identity token claims.",
        })),
        geoIpCountryCode: Type.Optional(Type.String({ description: "Geo-location IP country code." })),
        hashedFirstName: Type.Optional(Type.String({
            description: "SHA-256 Hashed value of the customer's first name provided by identity token claims.",
        })),
        hashedLastName: Type.Optional(Type.String({
            description: "SHA-256 Hashed value of the customer's last name provided by identity token claims.",
        })),
        hashedEmail: Type.Optional(Type.String({
            description: "SHA-256 Hashed value of the customer's email address.",
        })),
        hashedPhoneNumber: Type.Optional(Type.String({
            description: "SHA-256 Hashed value of the customer's phone number.",
        })),
        isAuthenticated: Type.Boolean({
            description: "Indicates if the customer is authenticated.",
        }),
        customerSubscriptionCountry: Type.Optional(Type.String({
            description: "The customer's subscription country provided by identity token claims.",
        })),
        loyaltyTier: Type.Optional(Type.String({
            description: "The customer's loyalty tier, the value is in the format [COUNTRY]_LOYT_[TIER_NUMBER]. undefined when no loyalty tier present in claims.",
        })),
    }, {
        description: "The customerIdentityData event is triggered when the customer identity data is retrieved by the consuming app. It provides the customer's identity data.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerIdentityData.json",
        additionalProperties: false,
    })),
    add_to_wishlist: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_to_wishlist", readOnly: true })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The add_to_wishlist event is triggered when a customer clicks on a product tile's save for later CTA. It provides information about the product(s) added to the wishlist and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_to_wishlist.json",
        additionalProperties: false,
    })),
    purchase: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "purchase", readOnly: true })),
        page: Type.Optional(Type.String({ default: "orderConfirmed", readOnly: true })),
        context: Type.Optional(Type.Object({
            dateTimePair: Type.Optional(Type.String({
                pattern: "^([\\+-]?\\d{4}(?!\\d{2}\\b))((-?)((0[1-9]|1[0-2])(\\3([12]\\d|0[1-9]|3[01]))?|W([0-4]\\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\\d|[12]\\d{2}|3([0-5]\\d|6[1-6])))([T\\s]((([01]\\d|2[0-3])((:?)[0-5]\\d)?|24\\:?00)([\\.,]\\d+(?!:))?)?(\\17[0-5]\\d([\\.,]\\d+)?)?([zZ]|([\\+-])([01]\\d|2[0-3]):?([0-5]\\d)?)?)?)?$",
                description: "The date and time at which the order occurred. In ISO8601 format",
            })),
        }, { description: "Context object" })),
        order: Type.Object({
            orderReference: Type.Optional(Type.String({ description: "The order reference number." })),
            currency: Type.Optional(Type.String({
                description: "ISO 4216 of currency customer is shopping in.",
            })),
            sizeSchema: Type.Optional(Type.String({
                description: "Size schema customer used during customer journey.",
            })),
            totalSalesTax: Type.Optional(Type.Number({
                minimum: 0,
                description: "Total amount of sales tax payable by the customer (if applicable).",
            })),
            totalSalesTaxGbp: Type.Optional(Type.Number({
                minimum: 0,
                description: "Sales tax converted to GBP (if applicable).",
            })),
            totalPrice: Type.Optional(Type.Number({
                minimum: 0,
                description: "The total price of the bag (ItemsSubtotal + Delivery - Discount + TotalSalesTax).",
            })),
            totalPriceGbp: Type.Optional(Type.Number({
                minimum: 0,
                description: "The total price of the bag in GBP.",
            })),
            discountCode: Type.Optional(Type.Object({
                code: Type.Optional(Type.String({ description: "Discount code used." })),
                totalAmount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Total discount.",
                })),
                totalAmountGbp: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Total discount in GBP.",
                })),
            }, { description: "optional data if discount is used." })),
            delivery: Type.Optional(Type.Object({
                deliveryCountryIsoCode: Type.Optional(Type.String({ description: "3166-1 alpha-2 code." })),
                deliveryOptionId: Type.Optional(Type.Number({
                    description: "The integer id of the selected delivery option.",
                })),
                isSplitFulfilment: Type.Optional(Type.Boolean({
                    description: "True if some of the items on the order will be delivered on a different delivery to the one represented by the deliveryOptionId.",
                })),
                price: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The price of the delivery (exclusive of sales tax where it is explicitly paid).",
                })),
                priceGbp: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The price of the delivery (exclusive of sales tax where it is explicitly paid) in GBP.",
                })),
                salesTax: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Sales tax applied on delivery (if applicable).",
                })),
                salesTaxGbp: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Sales tax in GBP applied on delivery (if applicable).",
                })),
            }, { description: "Delivery Information Object." })),
            payment: Type.Optional(Type.Object({
                billingCity: Type.Optional(Type.String({
                    description: "Payment method used to pay for the order.",
                })),
                billingCountryIsoCode: Type.Optional(Type.String({
                    description: "The ISO 3166-1 alpha-2 code of the selected billing country.",
                })),
                billingPostCode: Type.Optional(Type.String({
                    description: "The post code of the billing address.",
                })),
                billingState: Type.Optional(Type.String({ description: "State or County." })),
                paymentMethods: Type.Optional(Type.Array(Type.Object({
                    paymentMethod: Type.String({
                        description: "Method of payment.",
                    }),
                    cardScheme: Type.Optional(Type.String({
                        description: "Card type used if paid by card.",
                    })),
                    voucherType: Type.Optional(Type.Union([
                        Type.Literal("Loyalty"),
                        Type.Literal("Gift card"),
                        Type.Literal("Standard"),
                        Type.Literal("Out of policy return"),
                    ], {
                        description: "Voucher type used in the order.",
                    })),
                    amount: Type.Optional(Type.Number({
                        description: "Voucher amount used in order.",
                    })),
                    amountRedeemable: Type.Optional(Type.Number({
                        description: "Amount redeemable PRIOR to this order.",
                    })),
                }, {
                    $schema: "http://json-schema.org/draft-07/schema#",
                    description: "The payment methods used to pay for the order.",
                    additionalProperties: false,
                }), {
                    description: "The payment methods used to pay for the order.",
                })),
            }, { description: "Payment information object" })),
        }, { description: "Order object" }),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            payment_type: Type.Optional(Type.String({
                description: "Payment method currently selected for use with the order.",
            })),
            transaction_id: Type.Optional(Type.String({
                description: "Order reference number of purchase.",
            })),
            coupon: Type.Optional(Type.String({
                description: "Discount code associated with the purchase (if applicable).",
            })),
            discount: Type.Optional(Type.Number({
                description: "The discount applied to the item in GBP.",
            })),
            discountLocalCurrency: Type.Optional(Type.Number({
                description: "The discount applied to the item in local currency.",
            })),
            shipping: Type.Optional(Type.Number({
                description: "Shipping cost in GBP (if applicable).",
            })),
            shippingLocalCurrency: Type.Optional(Type.Number({
                description: "Shipping cost in local currency (if applicable).",
            })),
            tax: Type.Optional(Type.Number({ description: "Tax cost in GBP (if applicable)." })),
            taxLocalCurrency: Type.Optional(Type.Number({
                description: "Tax cost in local currency (if applicable).",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({
                    description: "Mapped from productIID or productId.",
                })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The monetary value of the discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The string value of discount code applied.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The purchase event is triggered on loading of the order accepted/received page or refreshing of the order accepted/received page. It indicates that the customer has loaded the order confirmation page after successful order placement, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/purchase.json",
        additionalProperties: false,
    })),
    available_components: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "available_components", readOnly: true })),
        components: Type.Array(Type.Object({
            position: Type.Number({
                description: "The position of the component on the content page",
            }),
            component_name: Type.String({
                description: "The name of the component",
            }),
        }), {
            description: "An array of components that can load from content pages",
        }),
    }, {
        description: "A list of components that can load from content pages",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/available_components.json",
        additionalProperties: false,
    })),
    customVariables: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customVariables", readOnly: true })),
        customVariables: Type.Object({
            "1": Type.Object({
                name: Type.String({
                    default: "countryCode",
                    description: "The identifier name for the country code custom variable",
                }),
                value: Type.String({
                    description: "The ISO country code representing the customer's location",
                    examples: ["GB", "US"],
                }),
            }, { description: "The country code of the customer" }),
            "3": Type.Object({
                name: Type.String({
                    default: "asosDeviceType",
                    description: "The identifier name for the device type custom variable",
                }),
                value: Type.String({
                    description: "The type of device being used to access the site",
                    examples: ["desktop", "mobile", "tablet"],
                }),
            }, { description: "The device type used by the customer" }),
            "4": Type.Object({
                name: Type.String({
                    default: "storeCode",
                    description: "The identifier name for the store code custom variable",
                }),
                value: Type.String({
                    description: "The code identifying the specific store or marketplace",
                    examples: ["COM"],
                }),
            }, { description: "The store code identifier" }),
            "9": Type.Object({
                name: Type.String({
                    default: "pageType",
                    description: "The identifier name for the page type custom variable",
                }),
                value: Type.String({
                    description: "The category or type of the current page",
                }),
            }, { description: "The type of page being viewed" }),
        }, {
            description: "An object containing key-value pairs representing custom variables for ContentSquare.",
            additionalProperties: false,
        }),
    }, {
        description: "ContentSquare specific custom variables to describe the page to the datalayer",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/content_square_custom_variables.json",
        additionalProperties: false,
    })),
    experiment_activation: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "experiment_activation", readOnly: true })),
        experiments: Type.Array(Type.Object({
            key: Type.String({
                description: "The unique identifier for the experiment",
                examples: ["emf123"],
            }),
            variation: Type.String({
                description: "The variation of the experiment",
                examples: ["on", "off"],
            }),
        }), { description: "An array of experiments that have been activated" }),
    }, {
        description: "Experiment Activation is triggered when the code makes the decision for a user to be added to an experiment bucket",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/experiment_activation.json",
        additionalProperties: false,
    })),
    failed_event: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "failed_event", readOnly: true })),
        errors: Type.Optional(Type.Array(Type.Unknown(), {
            description: "List of errors in the format of dataLayer errors within ASOS web dataLayer",
        })),
        event_context: Type.Optional(Type.Unknown()),
    }, {
        description: "The failed_event is triggered when an event fails to process correctly. It provides information about the error and the context for the event.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/failed_event.json",
    })),
    cta_response: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "cta_response", readOnly: true })),
        cta_response_type: Type.Union([Type.Literal("error"), Type.Literal("success")], {
            description: "An indication of whether the action from the call to action reference was successful",
        }),
        cta_response_name: Type.String({
            minLength: 1,
            description: "The descriptive string that represents the result of the call to action of the event",
        }),
        cta_reference: Type.String({
            minLength: 1,
            description: "The descriptive string that represents the context of the event",
        }),
    }, {
        description: "The cta_response event is triggered once the response to a CTA has occurred; this could be an API response or a page load. It provides information about the success or failure of the CTA action.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/cta_response.json",
        additionalProperties: false,
    })),
    cta_click: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "cta_click", readOnly: true })),
        cta_reference: Type.String({
            minLength: 1,
            description: "A descriptive reference to the CTA, which can be a combination of the exact place where the CTA is located; information about the component in which the CTA is, like form, carousel, grid, list, etc; and the page where the CTA is.",
        }),
    }, {
        description: "The cta_click event is triggered when a customer clicks on a CTA (Call to Action) that doesn't trigger a subsequent page load. It provides information about which CTA was clicked for tracking engagement across the website.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/cta_click.json",
        additionalProperties: false,
    })),
    successful_login: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "successful_login", readOnly: true })),
        authentication_channel: Type.Union([
            Type.Literal("unknown"),
            Type.Literal("social"),
            Type.Literal("email"),
        ], {
            $schema: "http://json-schema.org/draft-07/schema#",
            description: "The channel used for authentication.",
        }),
    }, {
        description: "The successful_login event is triggered when a customer successfully logs in.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/successful_login.json",
        additionalProperties: false,
    })),
    successful_registration: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "successful_registration", readOnly: true })),
        authentication_channel: Type.Union([
            Type.Literal("unknown"),
            Type.Literal("social"),
            Type.Literal("email"),
        ], {
            $schema: "http://json-schema.org/draft-07/schema#",
            description: "The channel used for authentication.",
        }),
    }, {
        description: "The successful_registration event is triggered when a customer successfully signs up.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/successful_registration.json",
        additionalProperties: false,
    })),
}, {
    $schema: "http://json-schema.org/draft-07/schema#",
    $id: "https://asos.com/dataLayer/events.json",
    description: "The central registry of all data layer events used across ASOS web applications.",
});

const dataLayerEventKeys = Object.values(types.properties).map((schema) => schema.properties.event.default);

function getWindow() {
    return globalThis.window;
}

const pushToDataLayerSitechromeWrapper = async (definitions) => {
    var _a, _b;
    const { pushToDataLayer } = (_b = (_a = getWindow().asos) === null || _a === void 0 ? void 0 : _a.siteChrome) !== null && _b !== void 0 ? _b : {};
    if (pushToDataLayer) {
        return await pushToDataLayer(definitions);
    }
    return new Promise((resolve, reject) => {
        var _a, _b, _c;
        const timeoutId = setTimeout(() => {
            reject(new Error("Timeout: pushToDataLayer-ready event was not fired within 10 seconds"));
        }, 10000);
        (_c = (_b = (_a = getWindow().asos) === null || _a === void 0 ? void 0 : _a.eventBus) === null || _b === void 0 ? void 0 : _b.once) === null || _c === void 0 ? void 0 : _c.call(_b, "pushToDataLayer-ready", async () => {
            var _a, _b, _c;
            clearTimeout(timeoutId);
            try {
                const result = await ((_c = (_b = (_a = getWindow().asos) === null || _a === void 0 ? void 0 : _a.siteChrome) === null || _b === void 0 ? void 0 : _b.pushToDataLayer) === null || _c === void 0 ? void 0 : _c.call(_b, definitions));
                resolve(result);
            }
            catch (e) {
                reject(e);
            }
        });
    });
};

const defaults = (eventDefinition, eventType) => Value.Default(Index(types, [eventType]), eventDefinition);

const clean = (eventDefinition, eventType) => Value.Clean(Index(types, [eventType]), eventDefinition);

const validate = (eventDefinition, eventType) => {
    const errors = [...Value.Errors(Index(types, [eventType]), eventDefinition)];
    if (errors.length) {
        throw new DataLayerError({
            message: `Object is not a valid match to the ${eventType} schema`,
            errors
        });
    }
};

const handleFailedEvent = async (args, eventDefinition, error) => {
    if (eventDefinition.event === "failed_event") {
        return undefined;
    }
    error.message = `${error.message}: See full error.validationErrors for details`;
    args.logError(error);
    return processEvent("failed_event", {
        failed_event: {
            event_context: eventDefinition,
            errors: (error === null || error === void 0 ? void 0 : error.validationErrors) || []
        }
    }, args);
};

const viewportDimensions = async (definition, eventKey) => {
    const window = getWindow();
    if (eventKey === "pageData" && !definition.viewportDimensions) {
        if (typeof window !== "undefined") {
            const height = window.innerHeight || "";
            const width = window.innerWidth || "";
            definition.viewportDimensions =
                typeof width === "number" && typeof height === "number"
                    ? width + ":" + height
                    : null;
        }
        else {
            definition.viewportDimensions = null;
        }
    }
    return definition;
};

let e=-1;const t=t=>{addEventListener("pageshow",(n=>{n.persisted&&(e=n.timeStamp,t(n));}),!0);},n=(e,t,n,i)=>{let s,o;return r=>{t.value>=0&&(r||i)&&(o=t.value-(s??0),(o||void 0===s)&&(s=t.value,t.delta=o,t.rating=((e,t)=>e>t[1]?"poor":e>t[0]?"needs-improvement":"good")(t.value,n),e(t)));}},i=e=>{requestAnimationFrame((()=>requestAnimationFrame((()=>e()))));},s=()=>{const e=performance.getEntriesByType("navigation")[0];if(e&&e.responseStart>0&&e.responseStart<performance.now())return e},o=()=>{const e=s();return e?.activationStart??0},r=(t,n=-1)=>{const i=s();let r="navigate";e>=0?r="back-forward-cache":i&&(document.prerendering||o()>0?r="prerender":document.wasDiscarded?r="restore":i.type&&(r=i.type.replace(/_/g,"-")));return {name:t,value:n,rating:"good",delta:0,entries:[],id:`v5-${Date.now()}-${Math.floor(8999999999999*Math.random())+1e12}`,navigationType:r}};const h=(e,t,n={})=>{try{if(PerformanceObserver.supportedEntryTypes.includes(e)){const i=new PerformanceObserver((e=>{Promise.resolve().then((()=>{t(e.getEntries());}));}));return i.observe({type:e,buffered:!0,...n}),i}}catch{}};let u=-1;const l=new Set,m=()=>"hidden"!==document.visibilityState||document.prerendering?1/0:0,p=e=>{if("hidden"===document.visibilityState){if("visibilitychange"===e.type)for(const e of l)e();isFinite(u)||(u="visibilitychange"===e.type?e.timeStamp:0,removeEventListener("prerenderingchange",p,!0));}},v=()=>{if(u<0){const e=o(),n=document.prerendering?void 0:globalThis.performance.getEntriesByType("visibility-state").filter((t=>"hidden"===t.name&&t.startTime>e))[0]?.startTime;u=n??m(),addEventListener("visibilitychange",p,!0),addEventListener("prerenderingchange",p,!0),t((()=>{setTimeout((()=>{u=m();}));}));}return {get firstHiddenTime(){return u},onHidden(e){l.add(e);}}},g=e=>{document.prerendering?addEventListener("prerenderingchange",(()=>e()),!0):e();},y=[1800,3e3],E=(e,s={})=>{g((()=>{const c=v();let a,d=r("FCP");const f=h("paint",(e=>{for(const t of e)"first-contentful-paint"===t.name&&(f.disconnect(),t.startTime<c.firstHiddenTime&&(d.value=Math.max(t.startTime-o(),0),d.entries.push(t),a(!0)));}));f&&(a=n(e,d,y,s.reportAllChanges),t((t=>{d=r("FCP"),a=n(e,d,y,s.reportAllChanges),i((()=>{d.value=performance.now()-t.timeStamp,a(!0);}));})));}));};const H=[800,1800],O=e=>{document.prerendering?g((()=>O(e))):"complete"!==document.readyState?addEventListener("load",(()=>O(e)),!0):setTimeout(e);},$=(e,i={})=>{let c=r("TTFB"),a=n(e,c,H,i.reportAllChanges);O((()=>{const d=s();d&&(c.value=Math.max(d.responseStart-o(),0),c.entries=[d],a(!0),t((()=>{c=r("TTFB",0),a=n(e,c,H,i.reportAllChanges),a(!0);})));}));};

const TTFB_TIMEOUT_MS = 1000;
const measureTTFB = async () => new Promise(resolve => {
    $(metric => {
        resolve(Math.round(metric.value));
    });
    setTimeout(() => resolve(null), TTFB_TIMEOUT_MS);
});
const getTTFBForDataLayer = async () => {
    try {
        const ttfbValue = await measureTTFB();
        if (ttfbValue !== null) {
            return {
                timeToFirstByteData: {
                    value: ttfbValue
                }
            };
        }
    }
    catch (_a) {
        return undefined;
    }
};
const timeToFirstByteEvent = async (definition, eventKey) => {
    if (eventKey !== "pageData" || typeof getWindow() === "undefined") {
        return { definition, additionalEvents: undefined };
    }
    return {
        definition,
        additionalEvents: await getTTFBForDataLayer()
    };
};

class MiddlewareManager {
    constructor() {
        this.eventMiddlewares = {};
        this.additionalEventsQueue = [];
    }
    useForEvent(eventKey, middleware) {
        if (!this.eventMiddlewares[eventKey]) {
            this.eventMiddlewares[eventKey] = [];
        }
        this.eventMiddlewares[eventKey].push(middleware);
    }
    async run(definition, eventKey, args) {
        let result = definition;
        if (this.eventMiddlewares[eventKey]) {
            for (const mw of this.eventMiddlewares[eventKey]) {
                const middlewareResult = await mw(result, eventKey, args);
                if (this.isMiddlewareResult(middlewareResult)) {
                    result = middlewareResult.definition;
                    if (middlewareResult.additionalEvents) {
                        this.additionalEventsQueue.push(middlewareResult.additionalEvents);
                    }
                }
                else {
                    result = middlewareResult;
                }
            }
        }
        return result;
    }
    isMiddlewareResult(result) {
        return (typeof result === "object" && result !== null && "definition" in result);
    }
    getAndClearAdditionalEvents() {
        const events = [...this.additionalEventsQueue];
        this.additionalEventsQueue = [];
        return events;
    }
}

const FCP_TIMEOUT_MS = 1000;
const measureFCP = async () => new Promise(resolve => {
    E(metric => {
        resolve(Math.round(metric.value));
    });
    setTimeout(() => resolve(null), FCP_TIMEOUT_MS);
});
const getFCPForDataLayer = async () => {
    try {
        const fcpValue = await measureFCP();
        if (fcpValue !== null) {
            return {
                firstContentfulPaintData: {
                    value: fcpValue
                }
            };
        }
    }
    catch (_a) {
        return undefined;
    }
};
const firstContentfulPaintEvent = async (definition, eventKey) => {
    if (eventKey !== "pageData" || typeof getWindow() === "undefined") {
        return { definition, additionalEvents: undefined };
    }
    return {
        definition,
        additionalEvents: await getFCPForDataLayer()
    };
};

/**
 * Checks for Analytics.Config and returns the configuration
 * Ported from asos-web-analytics/src/platforms/cptbasic.sitechrome.js lines 10-22
 * Modified to return the config instead of dispatching events
 *
 * The analyticsConfig is provided by AnalyticsViewModel via content pages/sitecore
 * The underlying model code has been preserved as a temporary measure to not lose analytics functionality
 * It contains an elementsList string which is a comma-separated list of component names
 *
 * Example:
 * {
 *   elementsList: "hero,product container,footer",
 *   someOtherProperty: "value"
 * }
 *
 */
function checkForAnalyticsConfig() {
    const window = getWindow();
    if (!window) {
        return Promise.resolve(null);
    }
    return new Promise(resolve => {
        var _a;
        if ((_a = window.require) === null || _a === void 0 ? void 0 : _a.specified("Analytics.Config")) {
            window.require(["Analytics.Config"], function (analyticsConfig) {
                resolve(analyticsConfig);
            });
        }
        else {
            resolve(null);
        }
    });
}

function parseElementsList(elementsList) {
    return elementsList
        .split(",")
        .map(componentName => componentName.trim())
        .filter(componentName => componentName)
        .map((componentName, index) => ({
        position: index + 1,
        component_name: componentName
    }));
}
function hasElementsList(config) {
    return (config !== null &&
        typeof config === "object" &&
        "elementsList" in config &&
        typeof config.elementsList === "string");
}
async function getComponentsFromAnalytics() {
    const analyticsConfig = await checkForAnalyticsConfig();
    if (!hasElementsList(analyticsConfig)) {
        return null;
    }
    const { elementsList } = analyticsConfig;
    if (!elementsList) {
        return null;
    }
    return parseElementsList(elementsList);
}
const available_components = async (definition, eventKey) => {
    if (eventKey !== "pageData" || typeof getWindow() === "undefined") {
        return { definition, additionalEvents: undefined };
    }
    const components = await getComponentsFromAnalytics();
    if (!components) {
        return { definition, additionalEvents: undefined };
    }
    return {
        definition,
        additionalEvents: {
            available_components: {
                components
            }
        }
    };
};

/**
 * Hashes a string with crypto
 * @param stringToHash - string to be hashed
 * @returns hashed string
 */
const hashString = async (stringToHash) => {
    var _a, _b;
    if (!stringToHash) {
        return undefined;
    }
    const isEncryptionPossible = !!((_b = (_a = window.crypto) === null || _a === void 0 ? void 0 : _a.subtle) === null || _b === void 0 ? void 0 : _b.digest);
    if (!isEncryptionPossible) {
        return undefined;
    }
    else {
        const sanitisedValue = stringToHash.replace(/ /g, "").toLocaleLowerCase();
        const data = new TextEncoder().encode(sanitisedValue);
        const hashedBuffer = await window.crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashedBuffer));
        const hashedString = hashArray
            .map(bytes => bytes.toString(16).padStart(2, "0"))
            .join("");
        return hashedString;
    }
};

// Mapped from https://github.com/asosteam/asos-web-auth-client/blob/main/src/auth-client/types/lastAction.ts
const LAST_ACTION_STRINGS = [
    "none",
    "signUpSocial",
    "signUpEmail",
    "linkAccounts",
    "savedBillingAddress",
    "savedBillingAndDeliveryAddress",
    "signInSocial",
    "signInEmail"
];
const lastActionToString = (value) => { var _a; return (_a = LAST_ACTION_STRINGS[value]) !== null && _a !== void 0 ? _a : "none"; };
const buildCustomerIdentityData = async (isAuthenticated, getIdTokenClaimsAsync) => {
    const { customerId, isFirstTimeBuyer, firstName, lastName, homeCountry, lastAction, customerSubscriptionCountry, loyaltyTier } = await getIdTokenClaimsAsync();
    return {
        customerIdentityData: {
            isAuthenticated: await isAuthenticated(),
            customerId: customerId,
            lastAction: lastActionToString(lastAction),
            firstTimeBuyer: isFirstTimeBuyer,
            hashedFirstName: await hashString(firstName),
            hashedLastName: await hashString(lastName),
            geoIpCountryCode: homeCountry || undefined,
            customerSubscriptionCountry: customerSubscriptionCountry || undefined,
            loyaltyTier: loyaltyTier || undefined
        }
    };
};
const dependenciesArePresent = (getIdTokenClaimsAsync, isAuthenticated) => {
    return getIdTokenClaimsAsync && isAuthenticated;
};
const customerIdentityData = async (definition, eventKey, { isAuthenticated, getIdTokenClaimsAsync, logError }) => {
    if (eventKey !== "pageData" ||
        typeof getWindow() === "undefined" ||
        !dependenciesArePresent(getIdTokenClaimsAsync, isAuthenticated)) {
        return { definition, additionalEvents: undefined };
    }
    try {
        const additionalEvents = await buildCustomerIdentityData(isAuthenticated, getIdTokenClaimsAsync);
        return {
            definition,
            additionalEvents
        };
    }
    catch (error) {
        logError(new DataLayerError({
            message: `Failed to get identity claims in customerIdentityData: ${error}`
        }));
        return { definition, additionalEvents: undefined };
    }
};

const parseLoyaltyTier = (loyaltyTier) => {
    const match = loyaltyTier.match(/^([A-Za-z]+)_LOYT_(\d+)$/);
    return {
        country: (match === null || match === void 0 ? void 0 : match[1]) || null,
        tierNumber: (match === null || match === void 0 ? void 0 : match[2]) ? parseInt(match[2], 10) : null
    };
};
const getCustomerLoyaltyData = (loyaltyTier) => {
    if (!loyaltyTier) {
        return {
            consentStatus: null,
            currentTierId: null,
            tierNumber: null,
            country: null
        };
    }
    const { country, tierNumber } = parseLoyaltyTier(loyaltyTier);
    return {
        consentStatus: "OptedIn",
        currentTierId: loyaltyTier,
        tierNumber,
        country
    };
};
const customerLoyaltyData = async (definition, eventKey) => {
    if (eventKey !== "customerIdentityData" ||
        typeof getWindow() === "undefined") {
        return { definition, additionalEvents: undefined };
    }
    return {
        definition,
        additionalEvents: {
            customerLoyaltyData: getCustomerLoyaltyData(definition.loyaltyTier)
        }
    };
};

// Helper for handling auth client events
class AuthEventHelper {
    constructor() {
        this.loginEventFired = false;
        this.loginPromise = new Promise((resolve, reject) => {
            if (typeof window !== "undefined") {
                window.addEventListener("AsosAuthClient.onLoggedIn", () => {
                    this.loginEventFired = true;
                    resolve();
                });
            }
            else {
                reject();
            }
        });
    }
    async waitForLogin(timeoutMs = 200) {
        if (this.loginEventFired)
            return true;
        try {
            await Promise.race([
                this.loginPromise,
                new Promise((_, reject) => {
                    setTimeout(reject, timeoutMs);
                })
            ]);
            return true;
        }
        catch (_a) {
            return false;
        }
    }
}
// Export a singleton instance
const authEventHelper = new AuthEventHelper();

const getAuthenticationChannel$1 = (lastAction) => {
    switch (lastAction) {
        case "signInSocial":
            return "social";
        case "signInEmail":
            return "email";
        default:
            return "unknown";
    }
};
const successful_login = async (definition, eventKey) => {
    if (eventKey !== "customerIdentityData" ||
        typeof getWindow() === "undefined") {
        return { definition, additionalEvents: undefined };
    }
    const loginOccurred = await authEventHelper.waitForLogin();
    if (!loginOccurred) {
        return { definition, additionalEvents: undefined };
    }
    const authenticationChannel = getAuthenticationChannel$1(definition.lastAction);
    if (authenticationChannel === "unknown") {
        return { definition, additionalEvents: undefined };
    }
    return {
        definition,
        additionalEvents: {
            successful_login: {
                authentication_channel: authenticationChannel
            }
        }
    };
};

const getAuthenticationChannel = (lastAction) => {
    switch (lastAction) {
        case "signUpSocial":
            return "social";
        case "signUpEmail":
            return "email";
        default:
            return "unknown";
    }
};
const successful_registration = async (definition, eventKey) => {
    if (eventKey !== "customerIdentityData" ||
        typeof getWindow() === "undefined") {
        return { definition, additionalEvents: undefined };
    }
    const registrationOccurred = await authEventHelper.waitForLogin();
    if (!registrationOccurred) {
        return { definition, additionalEvents: undefined };
    }
    const authenticationChannel = getAuthenticationChannel(definition.lastAction);
    if (authenticationChannel === "unknown") {
        return { definition, additionalEvents: undefined };
    }
    return {
        definition,
        additionalEvents: {
            successful_registration: {
                authentication_channel: authenticationChannel
            }
        }
    };
};

const middlewareManager = new MiddlewareManager();
// Field Middleware
middlewareManager.useForEvent("pageData", viewportDimensions);
// Additional Event Middleware
middlewareManager.useForEvent("pageData", timeToFirstByteEvent);
middlewareManager.useForEvent("pageData", firstContentfulPaintEvent);
middlewareManager.useForEvent("pageData", available_components);
middlewareManager.useForEvent("pageData", customerIdentityData);
middlewareManager.useForEvent("customerIdentityData", customerLoyaltyData);
middlewareManager.useForEvent("customerIdentityData", successful_login);
middlewareManager.useForEvent("customerIdentityData", successful_registration);

const processEvent = async (eventKey, definitions, args) => {
    let eventDefinition = defaults(definitions[eventKey], eventKey);
    eventDefinition = clean(eventDefinition, eventKey);
    eventDefinition = await middlewareManager.run(eventDefinition, eventKey, args);
    try {
        validate(eventDefinition, eventKey);
        return eventDefinition;
    }
    catch (error) {
        return await handleFailedEvent(args, eventDefinition, error);
    }
};

const pushToDataLayer = async (args, definitions) => {
    const { dataLayerSink, logError } = args;
    if (!dataLayerSink) {
        logError(new DataLayerError({ message: "Datalayer sink is not setup" }));
        return null;
    }
    const processedDefinitions = [];
    let eventKey;
    for (eventKey in definitions) {
        const eventDefinition = await processEvent(eventKey, definitions, args);
        if (eventDefinition !== undefined) {
            await dataLayerSink({
                events: [eventDefinition.event],
                data: eventDefinition
            });
            processedDefinitions.push(eventDefinition);
        }
    }
    const middlewareProcessedDefinitions = await processAdditionalEvents(args);
    return [...processedDefinitions, ...middlewareProcessedDefinitions];
};
const processAdditionalEvents = async (args) => {
    const processedDefinitions = [];
    const middlewareEventBatches = middlewareManager.getAndClearAdditionalEvents();
    const middlewarePromises = middlewareEventBatches.map(middlewareEventBatch => pushToDataLayer(args, middlewareEventBatch));
    const middlewareResults = await Promise.all(middlewarePromises);
    for (const middlewareProcessedDefinitions of middlewareResults) {
        if (middlewareProcessedDefinitions) {
            processedDefinitions.push(...middlewareProcessedDefinitions);
        }
    }
    return processedDefinitions;
};

export { DataLayerError, dataLayerEventKeys, pushToDataLayer, pushToDataLayerSitechromeWrapper };
//# sourceMappingURL=index.js.map
